import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import {
  addCreditCard,
  selectCurrentCreditCardsSubscription,
  selectIsStripeBilling,
} from '../Plan/planSlice'
import { applyNewCreditCard } from '../../actions/planActions'
import { FormGroup, FormLabel, FormControl } from 'react-bootstrap'
import BottomMenu from './BottomMenu'
import * as StripeElements from 'react-stripe-elements'
import Spinner from 'sharedComponents/Spinner'
import { stripeToken } from 'modules/token'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import ShopifyLogo from '../../assets/images/logos/shopify.svg'

// Extract stripe components
const {
  injectStripe,
  StripeProvider,
  Elements,
  CardNumberElement,
  CardExpiryElement,
} = StripeElements

// CardCVCElement was renamed to CardCvcElement in v5.0.0
const CardCVCElement = (StripeElements as any).CardCVCElement || (StripeElements as any).CardCvcElement

const ContentStyle = {
  base: {
    color: '#32325d',
    lineHeight: '18px',
    fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
    fontSmoothing: 'antialiased',
    fontSize: '16px',
    '::placeholder': {
      color: '#aab7c4'
    }
  },
  invalid: {
    color: '#a94442',
    iconColor: '#a94442'
  }
}

function Payment({ onNext, onBack, stripe }) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()
  const { creditCards, subscription } = useSelector(selectCurrentCreditCardsSubscription)
  /**
   *
   * Handle form
   *
   */
  const {
    handleSubmit,
  } = useForm()

  /**
   * Submit input CC# to stripe and update redux store
   */
  const onSubmit = () => {
    if (loading) return
    setLoading(true)
    stripe.createToken().then(async (data) => {
      if (data.error) {
        setLoading(false)
        toast.error(data.error.message)
      }
      else {
        const resultAction = await dispatch(addCreditCard(data.token.id))
        setLoading(false)
        if (addCreditCard.fulfilled.match(resultAction)) {
          const card = resultAction.payload
          dispatch(applyNewCreditCard(creditCards, card, subscription))
          toast.success(`Your payment method for trial is updated (${card.brand} **** ${card.last4})`)
          onNext()
        } else if (addCreditCard.rejected.match(resultAction)) {
          const errorMsg = (resultAction.payload as string) || resultAction.error.message || 'Internal Error'
          toast.error(errorMsg)
        }
      }
    }).catch(() => {
      setLoading(false)
    })
  }

  const onSkip = () => {
    onNext()
  }

  return (
    <>
      <div className="tw-min-h-screen tw-pt-10 tw-pb-10 tw-border-t tw-border-slate-200">
        <div className="tw-px-4 md:tw-px-8">
          <div className="tw-hidden md:tw-block">
            <div className="tw-text-xl tw-font-bold tw-mb-4">Start your Entrepreneur Trial</div>
            <div className="tw-text-slate-500 tw-mb-4">
              Start your Entrepreneur 14 day free trial. Get full access to all analytics, AI insights, and reporting tools. No commitment — cancel anytime.
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="credit-card-form" style={{position: 'relative'}}>
              { loading ? <Spinner mask/> : null}
              <FormGroup>
                <FormLabel>Card Number</FormLabel>
                <CardNumberElement placeholder="Your Credit Card Number" style={ContentStyle} />
              </FormGroup>
              <br />
              <FormGroup>
                <FormLabel>Date of Expiry</FormLabel>
                <CardExpiryElement style={ContentStyle} />
              </FormGroup>
              <br />
              <FormGroup>
                <FormLabel>Card CVC/CVV</FormLabel>
                <CardCVCElement placeholder="Your Card's CVC/CVV" style={ContentStyle} />
              </FormGroup>
              <br />
            </div>
            <BottomMenu onPrev={onBack} onSkip={onSkip} submitOnNext={true} />
          </form>
        </div>
      </div>
    </>
  )
}

const StripeInjectedBody = injectStripe(Payment)

function WrappedBody(props) {
  const isStripeBilling = useSelector(selectIsStripeBilling)
  const { onNext, onBack } = props

  // Hooks must be called unconditionally before any early returns
  const [stripeLoaded, setStripeLoaded] = useState(!!window.Stripe)
  useEffect(() => {
    if (isStripeBilling && !stripeLoaded) {
      const script = document.createElement('script')
      document.head.appendChild(script)
      script.onload = () => setStripeLoaded(true)
      script.src = 'https://js.stripe.com/v3/'
    }
  }, [isStripeBilling, stripeLoaded])

  if (!isStripeBilling) {
    return (
      <>
        <div className="tw-min-h-screen tw-pt-10 tw-pb-10 tw-border-t tw-border-slate-200">
          <div className="tw-px-4 md:tw-px-8">
            <div className="md:tw-block">
              <div className="tw-text-xl tw-font-bold tw-mb-4">Start your Entrepreneur Trial</div>
              <div>
                <img src={ShopifyLogo}  width={200}/>
                <br />
                <h4>Subscribe with Shopify</h4>
                <div>Billing will be managed by your Shopify account</div>
              </div>
            </div>

            <BottomMenu onPrev={onBack} submitOnNext={true} onNext={onNext} />
          </div>
        </div>
      </>
    )
  }

  return stripeLoaded ? (
    <StripeProvider apiKey={stripeToken}>
      <Elements>
        <StripeInjectedBody {...props}/>
      </Elements>
    </StripeProvider>
  ) : <Spinner />
}

export default WrappedBody
